import { useEffect } from 'react';
import { HEADER_BUTTON_MODE } from '@hqo/hqo-miniapp-client-sdk';
import { useMiniappSdk } from 'hooks/use-miniapp-sdk.hook';

/**
 * Turns the native header into this view's back affordance for as long as it is mounted.
 *
 * The shell's header is a single shared thing: whatever a view last set stays set. So a view that wants a
 * back arrow has to claim it on mount and hand it back on unmount, otherwise the next screen inherits it.
 * Returning to CLOSE rather than clearing is deliberate — the root of the miniapp is an exit, and that is
 * what the user lands back on.
 *
 * Back-swipe is enabled alongside it so the gesture and the button agree; a header that goes back while
 * the swipe closes the whole miniapp is worse than neither.
 *
 * @param enabled false on desktop, where the view draws its own chrome and must not touch the native one.
 */
export const useNativeHistoryHeader = (enabled: boolean): void => {
  const client = useMiniappSdk();

  useEffect(() => {
    if (!client || !enabled) {
      return undefined;
    }

    client.header.setHeader('', HEADER_BUTTON_MODE.HISTORY);
    client.header.showHeader();
    client.swipe.enableBackSwipe();

    return () => {
      client.header.setHeader('', HEADER_BUTTON_MODE.CLOSE);
      client.swipe.disableBackSwipe();
    };
  }, [client, enabled]);
};
